import { useState } from 'react'
import { FormDialog } from '@/components/common'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formProblem } from '@/lib/api/fieldErrors'
import { firstOfMonth, todayLocal } from '@/lib/dates'
import { useAssignEmployeePayElements } from '@/features/payroll'
import type { EmployeePayElementView, PayElementView } from '@/features/payroll'

/** The kinds a person can hold an amount on, in the order a payslip reads them. */
const GROUPS = [
  { type: 'EARNING', title: 'Earnings' },
  { type: 'DEDUCTION', title: 'Deductions' },
  { type: 'EMPLOYER_CONTRIBUTION', title: 'Employer contributions' },
] as const

interface Row {
  element: PayElementView
  /** What is paid today, or null when nothing is. */
  current: number | null
}

/** The first day of next month — when a change agreed this month normally starts. */
function firstOfNextMonth(): string {
  const now = new Date()
  return firstOfMonth(new Date(now.getFullYear(), now.getMonth() + 1, 1))
}

function money(value: number): string {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * A person's whole package on one screen — every element they could be paid, today's figure
 * beside a box for the new one.
 *
 * <p>Setting up somebody's salary one element at a time meant five dialogs for five lines, each
 * with its own date, and a package that was briefly half old and half new if the third one was
 * refused. Here there is one date and one Save, and the server writes every changed row or
 * none of them.
 *
 * <p>Every box starts at today's figure, so "changed" means "different from what is paid now",
 * and only those rows are sent — an untouched row is not re-written with a new start date it
 * never had. A box left empty is untouched too; 0 is how a line is stopped, which is why it is
 * spelled out under the totals rather than inferred from a blank.
 *
 * <p>Only elements that take a person's own amount are listed: informational ones are never
 * paid, and percentage or formula ones would ignore the figure. The list asks the server which
 * those are ({@link PayElementView.amountProblem}) rather than guessing, so it cannot offer a
 * row the save would refuse.
 */
export function EditSalaryDialog({
  employeeId, open, onOpenChange, elements, lines,
}: {
  employeeId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  elements: PayElementView[]
  lines: EmployeePayElementView[]
}) {
  const save = useAssignEmployeePayElements(employeeId)
  const today = todayLocal()

  const rows: Row[] = elements
    .filter((element) => element.active && element.amountProblem == null
      && GROUPS.some((group) => group.type === element.elementType))
    .sort((a, b) => a.evaluationOrder - b.evaluationOrder || a.code.localeCompare(b.code))
    .map((element) => {
      const line = lines.find((candidate) => candidate.elementId === element.id
        && candidate.effectiveFrom <= today
        && (candidate.effectiveTo == null || candidate.effectiveTo >= today))
      return { element, current: line?.amount ?? null }
    })

  const [effectiveFrom, setEffectiveFrom] = useState(firstOfNextMonth)
  const [notes, setNotes] = useState('')
  // Keyed by element id. Seeded lazily from the rows: the dialog is mounted only while open,
  // so each opening starts from what is paid at that moment.
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(rows.map((row) => [row.element.id, row.current == null ? '' : String(row.current)])))
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  /** The figure a row would pay after saving: the typed one, or today's when left blank. */
  function after(row: Row): number {
    const typed = amounts[row.element.id]?.trim()
    if (typed) {
      const value = Number(typed)
      return Number.isFinite(value) ? value : 0
    }
    return row.current ?? 0
  }

  function changed(row: Row): boolean {
    const typed = amounts[row.element.id]?.trim()
    if (!typed) return false
    const value = Number(typed)
    // 0 on a line nobody is paid is no change at all, and sending it would ask the server to
    // stop something that never started.
    return row.current == null ? value !== 0 : value !== row.current
  }

  const total = (type: string) => rows
    .filter((row) => row.element.elementType === type)
    .reduce((sum, row) => sum + after(row), 0)
  const gross = total('EARNING')
  const deductions = total('DEDUCTION')
  const employer = total('EMPLOYER_CONTRIBUTION')
  const changes = rows.filter(changed)

  async function submit() {
    setError(null)
    setRowErrors({})
    if (changes.length === 0) {
      setError('Nothing has changed — type a new amount on the lines that change.')
      return
    }
    const invalid = changes.find((row) => {
      const value = Number(amounts[row.element.id])
      return !Number.isFinite(value) || value < 0
    })
    if (invalid) {
      setRowErrors({ [invalid.element.id]: 'Enter an amount of zero or more.' })
      setError('One line needs attention — see the message on it.')
      return
    }
    try {
      await save.mutateAsync({
        effectiveFrom,
        notes: notes.trim() || null,
        items: changes.map((row) => ({
          elementId: row.element.id, amount: Number(amounts[row.element.id]),
        })),
      })
      onOpenChange(false)
    } catch (caught) {
      // The server names rows by their place in what was sent — items[2].amount — so the
      // index is turned back into the element it was, and the message goes on that line.
      const problem = formProblem(caught, 'The salary could not be saved.', { keepPath: true })
      const byElement: Record<string, string> = {}
      for (const [field, message] of Object.entries(problem.fields)) {
        const index = /^items\[(\d+)]/.exec(field)?.[1]
        const row = index == null ? undefined : changes[Number(index)]
        if (row && !(row.element.id in byElement)) byElement[row.element.id] = message
      }
      setRowErrors(byElement)
      const count = Object.keys(byElement).length
      setError(count === 0
        ? problem.summary
        : count === 1
          ? 'One line needs attention — see the message on it.'
          : `${count} lines need attention — see the messages on them.`)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit salary"
      description="Every changed line starts on the date below; the amount before it ends the day before. All lines are saved together, or none are."
      submitLabel="Save"
      size="lg"
      loading={save.isPending}
      error={error}
      onSubmit={() => { void submit() }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="salary-from">Effective from</Label>
          <DatePicker
            id="salary-from"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="salary-notes">Notes</Label>
          <Input
            id="salary-notes"
            value={notes}
            placeholder="Why it changed"
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </div>

      {rows.length === 0 && (
        <p className="mt-4 text-sm text-[var(--color-muted)]">
          No pay element takes a person&apos;s own amount yet. Set them up under Payroll › Pay elements.
        </p>
      )}

      {GROUPS.map((group) => {
        const groupRows = rows.filter((row) => row.element.elementType === group.type)
        if (groupRows.length === 0) return null
        return (
          <section key={group.type} className="mt-4" aria-label={group.title}>
            <h4 className="mb-1 text-xs font-semibold uppercase text-[var(--color-muted)]">{group.title}</h4>
            <table className="w-full text-sm">
              <thead className="text-xs text-[var(--color-muted)]">
                <tr>
                  <th className="p-1 text-start font-medium">Element</th>
                  <th className="p-1 text-end font-medium">Now</th>
                  <th className="w-40 p-1 text-end font-medium">New amount</th>
                </tr>
              </thead>
              <tbody>
                {groupRows.map((row) => {
                  const id = `salary-${row.element.id}`
                  const rowError = rowErrors[row.element.id]
                  return (
                    <tr key={row.element.id} className="border-t border-[var(--color-border)] align-top">
                      <td className="p-1">
                        <label htmlFor={id} className="text-[var(--color-heading)]">{row.element.name}</label>
                        <span className="ms-2 font-mono text-xs text-[var(--color-muted)]">{row.element.code}</span>
                      </td>
                      <td className="p-1 text-end tabular-nums text-[var(--color-muted)]">
                        {row.current == null ? '—' : money(row.current)}
                      </td>
                      <td className="p-1">
                        <Input
                          id={id}
                          type="number"
                          min="0"
                          step="0.01"
                          className="text-end"
                          aria-invalid={rowError ? true : undefined}
                          aria-describedby={rowError ? `${id}-error` : undefined}
                          value={amounts[row.element.id] ?? ''}
                          onChange={(event) =>
                            setAmounts({ ...amounts, [row.element.id]: event.target.value })}
                        />
                        {rowError && (
                          <p id={`${id}-error`} className="mt-1 text-xs text-[var(--color-danger)]">{rowError}</p>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </section>
        )
      })}

      {rows.length > 0 && (
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-[var(--color-border)] p-3 text-sm sm:grid-cols-4">
          <div><dt className="text-xs text-[var(--color-muted)]">Gross earnings</dt><dd data-testid="salary-gross" className="tabular-nums">{money(gross)}</dd></div>
          <div><dt className="text-xs text-[var(--color-muted)]">Deductions</dt><dd data-testid="salary-deductions" className="tabular-nums">{money(deductions)}</dd></div>
          <div><dt className="text-xs text-[var(--color-muted)]">Net</dt><dd data-testid="salary-net" className="font-semibold tabular-nums">{money(gross - deductions)}</dd></div>
          <div><dt className="text-xs text-[var(--color-muted)]">Employer contributions</dt><dd data-testid="salary-employer" className="tabular-nums">{money(employer)}</dd></div>
        </dl>
      )}
      <p className="mt-2 text-xs text-[var(--color-muted)]">
        {changes.length === 1 ? '1 line will change.' : `${changes.length} lines will change.`}{' '}
        Leave a box as it is to keep that line; enter 0 to stop paying it from the date.
      </p>
    </FormDialog>
  )
}
